import { Badge, Button, Card, CardContent, cn } from '@repo/ui'
import { createFileRoute } from '@tanstack/react-router'
import {
  AlertTriangle,
  Check,
  CheckCircle,
  Info,
  Trash2,
  TruckIcon,
  X,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useDocumentTitle } from '@/hooks/use-document-title'
import { localizedPageTitle } from '@/lib/page-title'

export const Route = createFileRoute('/dashboard/notifications/')({
  head: () => ({ meta: [{ title: localizedPageTitle('notifications') }] }),
  component: NotificationsPage,
})

type NotificationType = 'warning' | 'success' | 'info' | 'alert'
type NotificationFilter = 'all' | 'unread' | 'read'

interface Notification {
  id: number
  type: NotificationType
  title: string
  message: string
  time: string
  unread: boolean
}

export default function NotificationsPage() {
  useDocumentTitle('notifications')
  const { t } = useTranslation('notifications')
  const [filter, setFilter] = useState<NotificationFilter>('all')
  const [notifications, setNotifications] = useState<Array<Notification>>([
    {
      id: 1,
      type: 'warning',
      title: 'Maintenance Required',
      message:
        'Vehicle ABC-123 needs service in 500km. Schedule maintenance to avoid potential issues.',
      time: '5 min ago',
      unread: true,
    },
    {
      id: 2,
      type: 'success',
      title: 'Route Completed',
      message:
        'Driver John Doe successfully completed route #4521 ahead of schedule.',
      time: '1 hour ago',
      unread: true,
    },
    {
      id: 3,
      type: 'info',
      title: 'Driver Break Required',
      message:
        'Driver Sarah Smith needs mandatory break according to tachograph regulations.',
      time: '2 hours ago',
      unread: false,
    },
    {
      id: 4,
      type: 'alert',
      title: 'Vehicle Status Update',
      message: 'Vehicle XYZ-789 is now available and ready for assignment.',
      time: '3 hours ago',
      unread: false,
    },
    {
      id: 5,
      type: 'warning',
      title: 'Fuel Level Low',
      message:
        'Vehicle DEF-456 has low fuel level (15%). Refueling recommended.',
      time: '5 hours ago',
      unread: false,
    },
    {
      id: 6,
      type: 'success',
      title: 'Task Completed',
      message:
        'Delivery task #8923 has been completed successfully by driver Mike Johnson.',
      time: '1 day ago',
      unread: false,
    },
    {
      id: 7,
      type: 'info',
      title: 'Route Update',
      message: 'Route #4522 has been updated with new delivery stops.',
      time: '1 day ago',
      unread: false,
    },
    {
      id: 8,
      type: 'alert',
      title: 'Driver Hours Alert',
      message: 'Driver Emma Wilson is approaching maximum driving hours limit.',
      time: '2 days ago',
      unread: false,
    },
  ])

  const getNotificationIcon = (type: NotificationType) => {
    switch (type) {
      case 'warning':
        return AlertTriangle
      case 'success':
        return CheckCircle
      case 'info':
        return Info
      case 'alert':
        return TruckIcon
    }
  }

  const getNotificationColor = (type: NotificationType) => {
    switch (type) {
      case 'warning':
        return 'text-yellow-500'
      case 'success':
        return 'text-green-500'
      case 'info':
        return 'text-blue-500'
      case 'alert':
        return 'text-red-500'
    }
  }

  const filteredNotifications = notifications.filter((notification) => {
    if (filter === 'unread') return notification.unread
    if (filter === 'read') return !notification.unread
    return true
  })

  const unreadCount = notifications.filter((n) => n.unread).length

  const toggleReadStatus = (id: number) => {
    setNotifications(
      notifications.map((n) => (n.id === id ? { ...n, unread: !n.unread } : n)),
    )
  }

  const deleteNotification = (id: number) => {
    setNotifications(notifications.filter((n) => n.id !== id))
  }

  const markAllAsRead = () => {
    setNotifications(notifications.map((n) => ({ ...n, unread: false })))
  }

  const deleteAllRead = () => {
    setNotifications(notifications.filter((n) => n.unread))
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t('title')}</h1>
        <p className="text-muted-foreground">{t('subtitle')}</p>
      </div>

      {/* Filters and Actions */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('all')}
          >
            {t('all')}
            <Badge variant="secondary" className="ml-2">
              {notifications.length}
            </Badge>
          </Button>
          <Button
            variant={filter === 'unread' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('unread')}
          >
            {t('unread')}
            {unreadCount > 0 && (
              <Badge variant="secondary" className="ml-2">
                {unreadCount}
              </Badge>
            )}
          </Button>
          <Button
            variant={filter === 'read' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('read')}
          >
            {t('read')}
            <Badge variant="secondary" className="ml-2">
              {notifications.length - unreadCount}
            </Badge>
          </Button>
        </div>

        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <Button variant="outline" size="sm" onClick={markAllAsRead}>
              <Check className="mr-2 h-4 w-4" />
              {t('markAllAsRead')}
            </Button>
          )}
          {notifications.some((n) => !n.unread) && (
            <Button variant="outline" size="sm" onClick={deleteAllRead}>
              <Trash2 className="mr-2 h-4 w-4" />
              {t('deleteAll')}
            </Button>
          )}
        </div>
      </div>

      {/* Notifications List */}
      <div className="space-y-3">
        {filteredNotifications.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Info className="text-muted-foreground mb-4 h-12 w-12" />
              <p className="text-muted-foreground text-center text-sm">
                {filter === 'unread'
                  ? t('noUnreadNotifications')
                  : filter === 'read'
                    ? t('noReadNotifications')
                    : t('noNotifications')}
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredNotifications.map((notification) => {
            const Icon = getNotificationIcon(notification.type)
            const iconColor = getNotificationColor(notification.type)

            return (
              <Card
                key={notification.id}
                className={cn(
                  'hover:bg-muted/50 transition-colors',
                  notification.unread && 'border-primary/50 bg-primary/5',
                )}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={cn('mt-0.5', iconColor)}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex items-start justify-between gap-2">
                        <h3
                          className={cn(
                            'text-sm leading-none font-semibold',
                            notification.unread
                              ? 'text-foreground'
                              : 'text-muted-foreground',
                          )}
                        >
                          {notification.title}
                        </h3>
                        {notification.unread && (
                          <div className="bg-primary mt-1 h-2 w-2 flex-shrink-0 rounded-full" />
                        )}
                      </div>
                      <p className="text-muted-foreground text-sm">
                        {notification.message}
                      </p>
                      <p className="text-muted-foreground text-xs">
                        {notification.time}
                      </p>
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => toggleReadStatus(notification.id)}
                        title={
                          notification.unread
                            ? t('markAsRead')
                            : t('markAsUnread')
                        }
                      >
                        {notification.unread ? (
                          <Check className="h-4 w-4" />
                        ) : (
                          <X className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive h-8 w-8"
                        onClick={() => deleteNotification(notification.id)}
                        title={t('deleteNotification')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )
          })
        )}
      </div>
    </div>
  )
}
